import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { serviceSchema, breadcrumbSchema } from '../../utils/structuredData';
import './ServicePage.css';

const UIUXDesign = () => {
  const serviceStructuredData = serviceSchema(
    'UI/UX Design for App & Web',
    'Design beautiful, intuitive interfaces that users love. Our design process focuses on user research, wireframing, and creating pixel-perfect designs that drive engagement.',
    'UI/UX Design'
  );

  const breadcrumbStructuredData = breadcrumbSchema([
    { name: 'Home', url: 'https://www.gettechupdate.com' },
    { name: 'Services', url: 'https://www.gettechupdate.com/#services' },
    { name: 'UI/UX Design', url: 'https://www.gettechupdate.com/services/ui-ux-design' }
  ]);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceStructuredData, breadcrumbStructuredData]
  };

  return (
    <>
      <SEO
        title="UI/UX Design Services in Bhopal, Indore | App & Website Design"
        description="Professional UI/UX design services in Bhopal, Indore, Delhi, Mumbai, Bangalore, and across India. User research, wireframing, prototyping, and pixel-perfect designs for apps and websites. Figma, Adobe XD, and modern design tools."
        keywords="UI/UX design, UI/UX design in Bhopal, UI/UX design in Indore, user interface design, user experience design, wireframing, prototyping, Figma design, Adobe XD, design systems, mobile app design, web design, app design services"
        url="https://www.gettechupdate.com/services/ui-ux-design"
        structuredData={combinedSchema}
      />
      <div className="service-page">
      <div className="service-hero">
        <div className="service-hero-content">
          <div className="service-icon-large">
            <i className="fas fa-palette"></i>
          </div>
          <h1>UI/UX Design for App & Web</h1>
          <p className="service-hero-subtitle">
            Design beautiful, intuitive interfaces that users love. Our design process focuses on user research, 
            wireframing, and creating pixel-perfect designs that drive engagement.
          </p>
        </div>
      </div>

      <div className="service-content">
        <section className="service-section">
          <h2>Exceptional UI/UX Design Services</h2>
          <p>
            Great design is more than just aesthetics—it's about creating experiences that users love and remember. 
            Our UI/UX design team combines creativity with user-centered design principles to create interfaces that 
            are not only beautiful but also intuitive, accessible, and conversion-focused.
          </p>
        </section>

        <section className="service-section">
          <h2>Our Design Services</h2>
          <div className="expertise-grid">
            <div className="expertise-item">
              <i className="fas fa-search"></i>
              <h3>User Research</h3>
              <p>Comprehensive user research including interviews, surveys, and analytics to understand user needs and behaviors.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-sitemap"></i>
              <h3>Information Architecture</h3>
              <p>Structuring content and functionality in a logical, intuitive way that guides users to their goals.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-drafting-compass"></i>
              <h3>Wireframing & Prototyping</h3>
              <p>Creating low and high-fidelity wireframes and interactive prototypes to test and refine designs.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-paint-brush"></i>
              <h3>Visual Design</h3>
              <p>Beautiful, modern visual designs with attention to typography, color, spacing, and visual hierarchy.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-mobile-alt"></i>
              <h3>Responsive Design</h3>
              <p>Designs that work seamlessly across all devices, from mobile phones to large desktop screens.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-vial"></i>
              <h3>Usability Testing</h3>
              <p>Testing designs with real users to identify issues and optimize the user experience before development.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Design Tools & Technologies</h2>
          <div className="tech-stack">
            <div className="tech-item">
              <i className="fab fa-figma"></i>
              <span>Figma</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-pencil-alt"></i>
              <span>Adobe XD</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-image"></i>
              <span>Sketch</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-palette"></i>
              <span>Adobe Photoshop</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-vector-square"></i>
              <span>Adobe Illustrator</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-cube"></i>
              <span>InVision</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-magic"></i>
              <span>Principle</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-code"></i>
              <span>Zeplin</span>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Our Design Process</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Discovery & Research</h3>
              <p>Understanding your business, users, competitors, and market to inform design decisions and strategy.</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>User Personas & Journey Mapping</h3>
              <p>Creating detailed user personas and mapping user journeys to identify pain points and opportunities.</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Information Architecture</h3>
              <p>Structuring content and features logically with sitemaps and user flows that guide users effectively.</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Wireframing</h3>
              <p>Creating low-fidelity wireframes to establish layout, hierarchy, and functionality without visual distractions.</p>
            </div>
            <div className="process-step">
              <div className="step-number">5</div>
              <h3>Visual Design</h3>
              <p>Designing high-fidelity mockups with colors, typography, imagery, and visual elements that reflect your brand.</p>
            </div>
            <div className="process-step">
              <div className="step-number">6</div>
              <h3>Prototyping & Testing</h3>
              <p>Building interactive prototypes and conducting usability tests to refine designs before development.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>What We Design</h2>
          <div className="app-types">
            <div className="app-type-card">
              <i className="fas fa-mobile-alt"></i>
              <h3>Mobile App UI/UX</h3>
              <p>Native iOS and Android app designs following platform guidelines for optimal user experience.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-laptop"></i>
              <h3>Web Application Design</h3>
              <p>Complete web app interfaces with dashboards, admin panels, and complex user workflows.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-globe"></i>
              <h3>Website Design</h3>
              <p>Beautiful, conversion-focused website designs for businesses, portfolios, and e-commerce.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-shopping-cart"></i>
              <h3>E-commerce Design</h3>
              <p>Online store designs optimized for product discovery, shopping experience, and checkout conversion.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-briefcase"></i>
              <h3>Dashboard Design</h3>
              <p>Data visualization and dashboard designs for analytics, reporting, and business intelligence.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-puzzle-piece"></i>
              <h3>Design Systems</h3>
              <p>Comprehensive design systems with component libraries, style guides, and design documentation.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Design Deliverables</h2>
          <div className="features-grid">
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>User Research Reports</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>User Personas</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>User Journey Maps</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Information Architecture</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Wireframes</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>High-Fidelity Mockups</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Interactive Prototypes</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Design Specifications</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Style Guides</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Component Libraries</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Usability Test Reports</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Design Assets</span>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Design Principles We Follow</h2>
          <div className="principles-grid">
            <div className="principle-item">
              <h3>User-Centered Design</h3>
              <p>Every design decision is made with the user's needs, goals, and context in mind.</p>
            </div>
            <div className="principle-item">
              <h3>Accessibility</h3>
              <p>Designs that are usable by everyone, including people with disabilities, following WCAG guidelines.</p>
            </div>
            <div className="principle-item">
              <h3>Consistency</h3>
              <p>Consistent design patterns and interactions that create familiarity and reduce learning curve.</p>
            </div>
            <div className="principle-item">
              <h3>Simplicity</h3>
              <p>Clean, uncluttered designs that focus on essential elements and reduce cognitive load.</p>
            </div>
            <div className="principle-item">
              <h3>Visual Hierarchy</h3>
              <p>Strategic use of size, color, and spacing to guide user attention and prioritize information.</p>
            </div>
            <div className="principle-item">
              <h3>Feedback & Response</h3>
              <p>Clear visual feedback for user actions to confirm interactions and provide guidance.</p>
            </div>
          </div>
        </section>

        <section className="service-cta">
          <h2>Ready to Design Your Interface?</h2>
          <p>Let's create a design that users will love</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Get A Free Quote</Link>
            <Link to="/contact" className="btn-secondary">View Our Portfolio</Link>
          </div>
        </section>
      </div>
    </div>
    </>
  );
};

export default UIUXDesign;

